/**
 * Base Export Service
 * 
 * Provides common functionality for exporting report data to CSV, Excel, and JSON formats.
 */
export class BaseExportService {
  constructor(data, metadata = {}) {
    this.data = data;
    this.metadata = metadata;
    this.libraries_loaded = {
      papa_parse: false,
      sheetjs: false,
    };
  }

  /**
   * Load external script dynamically
   */
  async load_script(src) {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existing_script = document.querySelector(`script[src="${src}"]`);
      if (existing_script) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Load required export libraries
   */
  async load_export_libraries() {
    // Load Papa Parse for CSV
    if (!this.libraries_loaded.papa_parse && !window.Papa) {
      try {
        await this.load_script('https://unpkg.com/papaparse@5.3.2/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      } catch (error) {
        console.error('Failed to load Papa Parse:', error);
        throw new Error('Failed to load CSV export library');
      }
    }

    // Load SheetJS for Excel
    if (!this.libraries_loaded.sheetjs && !window.XLSX) {
      try {
        await this.load_script('https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      } catch (error) {
        console.error('Failed to load SheetJS:', error);
        throw new Error('Failed to load Excel export library');
      }
    }
  }

  /**
   * Generate timestamp for file names
   */
  get_timestamp() {
    return new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
  }

  /**
   * Download a blob as a file
   */
  download_file(blob, filename) {
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Strip HTML tags from string
   */
  strip_html(html_string) {
    if (!html_string) return '';
    const temp_div = document.createElement('div');
    temp_div.innerHTML = html_string;
    return temp_div.textContent || temp_div.innerText || '';
  }

  /**
   * Export data in the specified format
   */
  async export_data(format, export_options) {
    await this.load_export_libraries();

    const timestamp = this.get_timestamp();
    const filename = `${this.get_filename_prefix()}_${timestamp}`;

    const prepared_data = this.prepare_export_data(export_options);

    switch (format) {
      case 'csv':
        await this.export_csv(prepared_data, filename);
        break;
      case 'excel':
        await this.export_excel(prepared_data, filename);
        break;
      case 'json':
        await this.export_json(prepared_data, filename);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Export to CSV
   */
  async export_csv(data, filename) {
    if (!window.Papa) {
      throw new Error('CSV export library not loaded');
    }

    const Papa = window.Papa;

    // Export each section as a separate CSV file
    Object.keys(data).forEach((section_key) => {
      const section_data = data[section_key];
      
      if (Array.isArray(section_data) && section_data.length > 0) {
        const csv = Papa.unparse(section_data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const section_filename = `${filename}_${section_key}.csv`;
        this.download_file(blob, section_filename);
      }
    });
  }

  /**
   * Export to Excel
   */
  async export_excel(data, filename) {
    if (!window.XLSX) {
      throw new Error('Excel export library not loaded');
    }

    const XLSX = window.XLSX;
    const workbook = XLSX.utils.book_new();

    // Create a worksheet for each section
    Object.keys(data).forEach((section_key) => {
      const section_data = data[section_key];
      
      if (Array.isArray(section_data) && section_data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(section_data);
        // Limit sheet name to 31 characters (Excel requirement)
        const sheet_name = section_key.substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheet_name);
      }
    });

    // Write the workbook
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  }

  /**
   * Export to JSON
   */
  async export_json(data, filename) {
    const json_string = JSON.stringify(data, null, 2);
    const blob = new Blob([json_string], { type: 'application/json;charset=utf-8;' });
    this.download_file(blob, `${filename}.json`);
  }

  // Methods to be overridden by subclasses
  get_filename_prefix() {
    return 'Report_Export';
  }

  get_available_sections() {
    return {};
  }

  get_default_export_options() {
    return {};
  }
  // eslint-disable-next-line no-unused-vars
  prepare_export_data(export_options) {
    return {};
  }
}
